/**
 * DN detail page (canvas Ord_DeliveryDetail).
 *
 *   breadcrumb + header (DnDetailHeader)
 *   tabs: Гол (DnOverview + DnLines + map) · Бараа · Маршрут · GPS · Түүх · Хавсралт | sidebar (dn-receiver-card)
 *   sticky action bar (DnActionBar)
 *
 * Every action reads the delivery action matrix (components/delivery/delivery-actions.ts) with the
 * server's `allowedActions`; an unavailable action stays visible, aria-disabled, with the reason.
 */

import { Box, Grid, Stack, Tabs, Text } from "@mantine/core"
import { ClockIcon, MapPinIcon, MapTrifoldIcon, PackageIcon, PaperclipIcon, PushPinIcon } from "@phosphor-icons/react"
import dayjs from "dayjs"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { DeliveryApi, FailedDeliveryApi } from "../../apis"
import { renderFullName } from "../../utils/render-name"
import { useDeliveryActions } from "../delivery/delivery-actions"
import { usePermission } from "../permission"
import { ProgressNoteModal } from "../pending-deliveries/ProgressNoteModal"
import { DnActionBar } from "./dn-action-bar"
import { DnAttachments } from "./dn-attachments"
import { DnDetailHeader } from "./dn-detail-header"
import { DnDetail, dnPriority, driverSnapshot, isLiveStatus, useDnRoutes } from "./dn-detail-utils"
import { DnLines } from "./dn-lines"
import { DnDivider, DnOverview, DnSectionTitle } from "./dn-overview"
import { DnDriverCard, DnLinkedCard, DnPriorityCard, DnReceiverCard } from "./dn-receiver-card"
import { DnRoutePanel } from "./dn-route-panel"
import { DnTimeline } from "./dn-timeline"
import classes from "./dn-detail.module.css"
import { DnReceiverMap } from "./DnReceiverMap"
import { DnReassignDialog } from "./reassign-dialog"
import { ReasonDialog } from "./reason-dialog"

interface Props {
  dn: DnDetail
  onBack?: () => void
  onRefresh?: () => void
}

export function DnDetailPage({ dn, onBack, onRefresh }: Props) {
  const { t } = useTranslation("delivery")
  const navigate = useNavigate()
  const { shownStateOf } = useDeliveryActions()
  // The driver list behind the reassign dialog (DeliveryApi.staffSelect) needs ORD_DN_ASSIGN on the BE.
  const assignPerm = usePermission("ORD_DN_ASSIGN")
  const handleBack = () => (onBack ? onBack() : navigate(-1))

  const [reassignOpen, setReassignOpen] = useState(false)
  const [cancelOpen, setCancelOpen] = useState(false)
  const [issueOpen, setIssueOpen] = useState(false)
  const [notifyOpen, setNotifyOpen] = useState(false)
  const [completeOpen, setCompleteOpen] = useState(false)

  const dnId = dn.id ?? ""
  const refresh = () => onRefresh?.()
  const { routes, isLoading: routesLoading } = useDnRoutes(dn.id ?? null)

  const channel = dn.classification?.deliveryChannel ?? null
  const lineCount = dn.lines?.length ?? 0
  const priority = dnPriority(dn)
  const isLive = isLiveStatus(dn.deliveryNoteStatus)
  const staffName = dn.staff && (dn.staff.lastName || dn.staff.firstName) ? renderFullName(dn.staff) : null

  const snapshot = driverSnapshot(dn)
  const lastSeen = snapshot.recordedAt ? t("dnDetail.minAgo", { count: Math.max(0, dayjs().diff(dayjs(snapshot.recordedAt), "minute")) }) : null
  const mapProps = {
    isLive,
    lastSeen,
    eta: dn.deliveryDate ? dayjs(dn.deliveryDate).format("HH:mm") : null,
    driverLat: snapshot.lat,
    driverLng: snapshot.lng,
    driverStage: snapshot.stage,
    driverName: staffName,
  }

  // One subject for every DN action: status, channel, BE preconditions, receipt, phone.
  const subject = {
    deliveryNoteStatus: dn.deliveryNoteStatus,
    deliveryChannel: channel,
    hasReceipt: dn.hasReceipt,
    receiverPhone: dn.receiverPhone ?? null,
    allowedActions: dn.allowedActions ?? null,
  }
  const reassignBase = shownStateOf("dnReassign", subject)
  const reassignState = assignPerm.hasAccess ? reassignBase : { ...reassignBase, allowed: false, reason: assignPerm.reason }

  return (
    <Stack gap="md">
      <DnDetailHeader
        dn={dn}
        priority={priority}
        channel={channel}
        cancelState={shownStateOf("cancel", subject)}
        onBack={handleBack}
        onCancel={() => setCancelOpen(true)}
      />

      <Grid gap="md">
        <Grid.Col span={{ base: 12, lg: 8 }}>
          <Box className={classes.tabsCard}>
            <Tabs defaultValue="main" keepMounted={false} color="dark">
              <Tabs.List px="md" pt="xs">
                <Tabs.Tab value="main" leftSection={<PushPinIcon size={16} />}>
                  {t("dnDetail.tabMain")}
                </Tabs.Tab>
                <Tabs.Tab value="items" leftSection={<PackageIcon size={16} />}>
                  {t("dnDetail.tabItems")}
                  {lineCount > 0 ? ` (${lineCount})` : ""}
                </Tabs.Tab>
                <Tabs.Tab value="route" leftSection={<MapTrifoldIcon size={16} />}>
                  {t("dnDetail.tabRoute")}
                </Tabs.Tab>
                <Tabs.Tab value="gps" leftSection={<MapPinIcon size={16} />}>
                  {t("dnDetail.tabGps")}
                </Tabs.Tab>
                <Tabs.Tab value="timeline" leftSection={<ClockIcon size={16} />}>
                  {t("dnDetail.tabHistory")}
                </Tabs.Tab>
                <Tabs.Tab value="attachments" leftSection={<PaperclipIcon size={16} />}>
                  {t("dnDetail.tabAttachments")}
                </Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="main" className={classes.tabPanel}>
                <DnOverview dn={dn} />
                <DnDivider />
                <DnLines dn={dn} />
                <DnDivider />
                <Stack gap="md">
                  <DnSectionTitle icon={<MapPinIcon size={18} />} title={t("dnDetail.map")} />
                  <DnReceiverMap {...mapProps} height={320} />
                </Stack>
              </Tabs.Panel>

              <Tabs.Panel value="items" className={classes.tabPanel}>
                <DnLines dn={dn} />
              </Tabs.Panel>

              <Tabs.Panel value="route" className={classes.tabPanel}>
                <DnRoutePanel dnId={dn.id ?? null} routes={routes} isLoading={routesLoading} />
              </Tabs.Panel>

              <Tabs.Panel value="gps" className={classes.tabPanel}>
                <Stack gap="sm">
                  <DnReceiverMap {...mapProps} height={520} />
                  {snapshot.lat == null && <Text className={classes.hint}>{t("dnDetail.gpsHint")}</Text>}
                </Stack>
              </Tabs.Panel>

              <Tabs.Panel value="timeline" className={classes.tabPanel}>
                <DnTimeline dn={dn} staffName={staffName} addNoteState={shownStateOf("addNote", subject)} />
              </Tabs.Panel>

              <Tabs.Panel value="attachments" className={classes.tabPanel}>
                {dnId && <DnAttachments dnId={dnId} initialPdf={dn.pdf} regenerateState={shownStateOf("regeneratePdf", subject)} />}
              </Tabs.Panel>
            </Tabs>
          </Box>
        </Grid.Col>

        <Grid.Col span={{ base: 12, lg: 4 }}>
          <Stack gap="md">
            <DnReceiverCard dn={dn} />
            <DnDriverCard
              dn={dn}
              staffName={staffName}
              isLive={isLive}
              route={routes[0] ?? null}
              reassignState={reassignState}
              onReassign={() => setReassignOpen(true)}
            />
            <DnPriorityCard dn={dn} priority={priority} />
            <DnLinkedCard dn={dn} />
          </Stack>
        </Grid.Col>
      </Grid>

      <DnActionBar
        status={dn.deliveryNoteStatus}
        notifyState={shownStateOf("notifyBuyer", subject)}
        onNotify={() => setNotifyOpen(true)}
        issueState={shownStateOf("reportFailed", subject)}
        completeState={shownStateOf("manualComplete", subject)}
        onIssue={() => setIssueOpen(true)}
        onComplete={() => setCompleteOpen(true)}
      />

      {dnId && (
        <>
          <ProgressNoteModal dn={dn} opened={notifyOpen} onClose={() => setNotifyOpen(false)} />
          <DnReassignDialog
            opened={reassignOpen}
            onClose={() => setReassignOpen(false)}
            deliveryNoteId={dnId}
            refCode={dn.refCode}
            currentStaffId={dn.staffId ?? null}
            businessId={dn.businessId}
            onReassigned={refresh}
          />

          <ReasonDialog
            opened={cancelOpen}
            onClose={() => setCancelOpen(false)}
            title={t("dnDetail.cancelTitle")}
            description={t("dnDetail.cancelDesc")}
            confirmLabel={t("dnDetail.cancel")}
            confirmColor="brand"
            reasonLabel={t("dnDetail.cancelReasonLabel")}
            reasonPlaceholder={t("dnDetail.cancelReasonPlaceholder")}
            onConfirm={(reason) => DeliveryApi.cancelDelivery(dnId, reason).then(() => undefined)}
            onSucceeded={refresh}
          />

          <ReasonDialog
            opened={completeOpen}
            onClose={() => setCompleteOpen(false)}
            title={t("dnDetail.completeTitle")}
            description={t("dnDetail.completeDesc")}
            confirmLabel={t("dnDetail.completeConfirm")}
            confirmColor="brand"
            reasonLabel={t("dnDetail.completeReasonLabel")}
            reasonPlaceholder={t("dnDetail.completeReasonPlaceholder")}
            withPhotos
            onConfirm={(reason, photoUrls) => DeliveryApi.manualComplete(dnId, reason, photoUrls).then(() => undefined)}
            onSucceeded={refresh}
          />

          <ReasonDialog
            opened={issueOpen}
            onClose={() => setIssueOpen(false)}
            title={t("dnDetail.issueTitle")}
            description={t("dnDetail.issueDesc")}
            confirmLabel={t("dnDetail.issueConfirm")}
            confirmColor="brand"
            reasonLabel={t("dnDetail.issueReasonLabel")}
            reasonPlaceholder={t("dnDetail.issueReasonPlaceholder")}
            withPhotos
            onConfirm={(reason, photoUrls) =>
              FailedDeliveryApi.reportFailed(dnId, {
                exceptionType: "OTHER",
                description: reason,
                photoUrls: photoUrls.length > 0 ? photoUrls : undefined,
              }).then(() => undefined)
            }
            onSucceeded={refresh}
          />
        </>
      )}
    </Stack>
  )
}
